/**
 * Uptime Settings card. API token goes to workspace_secrets (UPTIME_API_TOKEN).
 * The token is never shown again after save.
 */
import { useCallback, useEffect, useState } from 'react'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SettingsSection } from '@/components/features/settings/settings-section'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'

interface UptimeIntegrationSectionProps {
  organizationId?: string | null
  canManage?: boolean
}

function orgQs(organizationId?: string | null): string {
  if (!organizationId) return ''
  return `?organizationId=${encodeURIComponent(organizationId)}`
}

export function UptimeIntegrationSection({
  organizationId,
  canManage = false,
}: UptimeIntegrationSectionProps) {
  const [hasToken, setHasToken] = useState(false)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState('')
  const [showToken, setShowToken] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!organizationId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await fetchWithAuth(`/api/uptime/status${orgQs(organizationId)}`)
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Could not load Uptime status')
        return
      }
      setHasToken(Boolean(data.has_token))
    } catch {
      setError('Could not load Uptime status')
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void load()
  }, [load])

  async function saveToken() {
    if (!organizationId || !token.trim()) return
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const res = await fetchWithAuth(`/api/uptime/token${orgQs(organizationId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId, token: token.trim() }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Could not save token')
        return
      }
      setToken('')
      setShowToken(false)
      setHasToken(true)
      setMessage('Token saved. Uptime accepted the key.')
    } catch {
      setError('Could not save token')
    } finally {
      setSaving(false)
    }
  }

  async function testConnection() {
    if (!organizationId) return
    setTesting(true)
    setError(null)
    setMessage(null)
    try {
      const res = await fetchWithAuth(`/api/uptime/test${orgQs(organizationId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Connection test failed')
        return
      }
      setMessage(`Connected. Sample check count: ${data.sample_count ?? 0}.`)
    } catch {
      setError('Connection test failed')
    } finally {
      setTesting(false)
    }
  }

  return (
    <SettingsSection
      title="API token"
      description="Account token from Uptime Settings, API. Stored encrypted for this workspace. Agents can read checks, outages, and alerts. They cannot pause or rename checks."
      data-testid={testIds.workspaceSettings.uptimeSection}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {hasToken ? 'A token is saved.' : 'No token saved yet.'}
          </p>
          {canManage ? (
            <div className="space-y-2">
              <Label htmlFor="uptime-api-token">API token</Label>
              <div className="relative max-w-xl">
                <Input
                  id="uptime-api-token"
                  type={showToken ? 'text' : 'password'}
                  value={token}
                  autoComplete="off"
                  placeholder={hasToken ? 'Paste a new token to replace' : 'Paste API token'}
                  onChange={(e) => setToken(e.target.value)}
                  data-testid={testIds.workspaceSettings.uptimeApiKey}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                  onClick={() => setShowToken((v) => !v)}
                  aria-label={showToken ? 'Hide token' : 'Show token'}
                >
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => void saveToken()}
                  disabled={saving || !token.trim()}
                  data-testid={testIds.workspaceSettings.uptimeSaveToken}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save token'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void testConnection()}
                  disabled={testing || !hasToken}
                  data-testid={testIds.workspaceSettings.uptimeTestConnection}
                >
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Test connection'}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">An admin saves the API token.</p>
          )}
          {message ? (
            <p className="text-sm" data-testid={testIds.workspaceSettings.uptimeStatusMessage}>
              {message}
            </p>
          ) : null}
          {error ? (
            <p className="text-sm text-destructive" data-testid={testIds.workspaceSettings.uptimeStatusMessage}>
              {error}
            </p>
          ) : null}
        </div>
      )}
    </SettingsSection>
  )
}
